import Seo from "@/components/Seo";
import { useState, useEffect, useMemo } from "react";
import { useNavigate, Routes, Route, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  Home, QrCode, Clock, LogOut, Menu, X, Search, Check,
  AlertCircle, User, Calendar, Ship, Waves, Fish, Wine, CreditCard, ExternalLink,
  Banknote, TrendingUp, Activity, Euro, MapPin, Copy, ArrowRight, ChevronRight,
  Bell, Camera, CameraOff
} from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer
} from "recharts";

import { PassAlgarveLogo, PassAlgarveLogoCompact } from "@/components/PassAlgarveLogo";

const ATTRACTION_ICONS = {
  "ponta-piedade": Ship,
  "benagil-cave": Waves,
  "dolphin-watching": Fish,
  "bon-vivant": Wine,
};

// =====================================================
// Connect Banner (compact, reused everywhere)
// =====================================================
const ConnectBanner = ({ status, onOnboard, loadingOnboard }) => {
  if (!status) return null;

  if (!status.has_account) {
    return (
      <Card className="bg-amber-50 border-amber-300 shadow-sm" data-testid="connect-banner-no-account">
        <CardContent className="pt-4 pb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center flex-shrink-0">
              <CreditCard className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <p className="font-heading text-amber-900 font-semibold">Conecte a sua conta Stripe</p>
              <p className="text-sm text-amber-800/80 font-body">
                Para receber pagamentos automáticos quando um cliente visitar a sua atração, complete o onboarding Stripe (~2 min).
              </p>
            </div>
          </div>
          <Button
            onClick={onOnboard}
            disabled={loadingOnboard}
            className="bg-amber-600 hover:bg-amber-700 text-white rounded-full flex-shrink-0"
            data-testid="connect-onboard-btn"
          >
            {loadingOnboard ? "Carregando..." : "Conectar Stripe"}
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!status.stripe_payouts_enabled || !status.stripe_details_submitted) {
    return (
      <Card className="bg-orange-50 border-orange-300 shadow-sm" data-testid="connect-banner-incomplete">
        <CardContent className="pt-4 pb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-200 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-orange-700" />
            </div>
            <div>
              <p className="font-heading text-orange-900 font-semibold">Onboarding incompleto</p>
              <p className="text-sm text-orange-800/80 font-body">
                {(status.stripe_requirements?.currently_due?.length || 0) > 0
                  ? `Pendentes: ${status.stripe_requirements.currently_due.slice(0, 3).join(", ")}${status.stripe_requirements.currently_due.length > 3 ? "..." : ""}`
                  : "Complete os passos na Stripe para começar a receber transferências."
                }
              </p>
            </div>
          </div>
          <Button
            onClick={onOnboard}
            disabled={loadingOnboard}
            className="bg-orange-600 hover:bg-orange-700 text-white rounded-full flex-shrink-0"
            data-testid="connect-resume-btn"
          >
            {loadingOnboard ? "Carregando..." : "Continuar"}
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-emerald-50 border-emerald-300 shadow-sm" data-testid="connect-banner-ok">
      <CardContent className="pt-3 pb-3 flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-emerald-200 flex items-center justify-center">
          <Check className="w-4 h-4 text-emerald-700" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-body text-emerald-900">
            <strong>Stripe conectado</strong> · Transferências ativas · <span className="font-mono text-xs">{status.stripe_account_id}</span>
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

// Hook: fetch connect status + provide onboarding action
const useConnectStatus = () => {
  const { token, API } = useAuth();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [onboardLoading, setOnboardLoading] = useState(false);

  const refresh = async () => {
    setError(null);
    try {
      const resp = await axios.get(`${API}/partner/connect/status`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });
      setStatus(resp.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Erro ao carregar estado Stripe");
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    refresh();
    const params = new URLSearchParams(window.location.search);
    if (params.get("connect") === "return") {
      toast.success("Retornou do onboarding Stripe");
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(refresh, 800);
    } else if (params.get("connect") === "refresh") {
      toast.info("Link expirado — gere novo link.");
      window.history.replaceState({}, "", window.location.pathname);
    }
    // eslint-disable-next-line
  }, []);

  const onOnboard = async () => {
    setOnboardLoading(true);
    try {
      const resp = await axios.post(`${API}/partner/connect/onboard`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = resp.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao iniciar onboarding");
      setOnboardLoading(false);
    }
  };

  return { status, error, loaded, onOnboard, onboardLoading, refresh };
};

// =====================================================
// Overview (Home) — KPIs, charts, recent activity
// =====================================================
const OverviewView = () => {
  const { token, API, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { status, onOnboard, onboardLoading } = useConnectStatus();

  useEffect(() => {
    (async () => {
      try {
        const resp = await axios.get(`${API}/partner/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStats(resp.data);
      } catch (e) {
        toast.error("Erro ao carregar estatísticas");
      } finally {
        setLoading(false);
      }
    })();
  }, [API, token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  const k = stats?.kpis || {};
  const daily = (stats?.daily_series || []).map(d => ({
    ...d,
    label: new Date(d.date).toLocaleDateString("pt-PT", { day: "2-digit", month: "short" }),
  }));
  const byAttr = stats?.by_attraction || [];

  const kpiCards = [
    { label: "Validações Hoje", value: k.today_visits || 0, Icon: Activity, color: "emerald", sub: "Visitas validadas hoje" },
    { label: "Últimos 7 dias", value: k.week_visits || 0, Icon: TrendingUp, color: "blue", sub: "vs período anterior" },
    { label: "Total Validações", value: k.total_visits || 0, Icon: Check, color: "gold", sub: "Histórico completo" },
    { label: "Receita Acumulada", value: `€ ${(k.revenue_accrued || 0).toFixed(2)}`, Icon: Euro, color: "purple", sub: "Soma de Net por visita" },
  ];

  const colorMap = {
    emerald: "bg-emerald-100 text-emerald-600",
    blue: "bg-blue-100 text-blue-600",
    gold: "bg-gold/15 text-gold",
    purple: "bg-purple-100 text-purple-600",
  };

  return (
    <div className="space-y-6">
      <Seo noindex title="Painel do Parceiro" />
      <div>
        <h1 className="font-heading text-3xl text-navy mb-1">Olá, {user?.name}</h1>
        <p className="text-navy/60 font-body">Visão geral do seu desempenho como parceiro PASS ALGARVE</p>
      </div>

      <ConnectBanner status={status} onOnboard={onOnboard} loadingOnboard={onboardLoading} />

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map(({ label, value, sub, Icon, color }) => (
          <Card key={label} className="bg-white border-gold/20 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="pt-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-navy/60 text-xs font-body uppercase tracking-wide">{label}</p>
                  <p className="font-heading text-2xl text-navy mt-1">{value}</p>
                  {sub && <p className="text-navy/40 text-[11px] mt-1 font-body">{sub}</p>}
                </div>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${colorMap[color]}`}>
                  <Icon className="w-5 h-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <button
          onClick={() => navigate("/partner/validate?scan=1")}
          className="group bg-gradient-to-br from-gold to-amber-500 text-white p-5 rounded-2xl shadow-md hover:shadow-xl text-left transition"
          data-testid="quick-validate"
        >
          <QrCode className="w-7 h-7 mb-2" />
          <p className="font-heading text-lg">Validar Passaporte</p>
          <p className="text-white/85 text-sm font-body mt-1">Scaneie ou digite o código do cliente</p>
          <span className="inline-flex items-center text-sm font-semibold mt-3">
            <Camera className="w-4 h-4 mr-1" /> Abrir Scanner
            <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </span>
        </button>

        <button
          onClick={() => navigate("/partner/history")}
          className="group bg-white border border-gold/20 p-5 rounded-2xl shadow-sm hover:shadow-md text-left transition"
          data-testid="quick-history"
        >
          <Clock className="w-7 h-7 mb-2 text-navy" />
          <p className="font-heading text-lg text-navy">Histórico</p>
          <p className="text-navy/60 text-sm font-body mt-1">{stats?.recent_visits?.length || 0} validações recentes</p>
          <span className="inline-flex items-center text-sm font-semibold mt-3 text-gold">
            Ver todas <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </span>
        </button>

        <button
          onClick={() => navigate("/partner/payments")}
          className="group bg-white border border-gold/20 p-5 rounded-2xl shadow-sm hover:shadow-md text-left transition"
          data-testid="quick-payments"
        >
          <Banknote className="w-7 h-7 mb-2 text-navy" />
          <p className="font-heading text-lg text-navy">Pagamentos Stripe</p>
          <p className="text-navy/60 text-sm font-body mt-1">
            {stats?.stripe?.payouts_enabled ? "Conta ativa para transferências" : "Configuração pendente"}
          </p>
          <span className="inline-flex items-center text-sm font-semibold mt-3 text-gold">
            Gerir <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </span>
        </button>
      </div>

      {/* Charts row: daily area + by-attraction bars */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-white border-gold/20 shadow-sm lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-xl text-navy">Validações (últimos 30 dias)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64" data-testid="partner-chart-daily">
              {daily.every(d => d.visits === 0) ? (
                <div className="h-full flex items-center justify-center text-navy/40 text-sm">
                  Sem validações nos últimos 30 dias
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={daily} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="grad-partner" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ background: "#0f172a", border: "none", borderRadius: 8, color: "#fff", fontSize: 12 }}
                      formatter={(v) => [v, "Validações"]}
                    />
                    <Area type="monotone" dataKey="visits" stroke="#D4AF37" strokeWidth={2} fill="url(#grad-partner)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-gold/20 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-xl text-navy">Por Atração</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64" data-testid="partner-chart-by-attraction">
              {byAttr.length === 0 ? (
                <div className="h-full flex items-center justify-center text-navy/40 text-sm text-center px-3">
                  Nenhuma atração atribuída a si. Contacte o admin.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byAttr} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 10, fill: "#64748b" }}
                      tickFormatter={(v) => v.length > 10 ? v.slice(0, 10) + "…" : v}
                    />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ background: "#0f172a", border: "none", borderRadius: 8, color: "#fff", fontSize: 12 }}
                      formatter={(v) => [v, "Visitas"]}
                    />
                    <Bar dataKey="visits" fill="#0d9488" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* My Attractions */}
      <Card className="bg-white border-gold/20 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-xl text-navy flex items-center gap-2">
            <MapPin className="w-5 h-5 text-gold" /> Minhas Atrações ({byAttr.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {byAttr.length === 0 ? (
            <div className="py-6 text-center text-navy/50 text-sm">
              Nenhuma atração atribuída. Contacte o administrador para associar uma atração ao seu utilizador.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {byAttr.map(a => {
                const Icon = ATTRACTION_ICONS[a.id] || MapPin;
                return (
                  <div key={a.id} className="p-4 bg-gray-50 rounded-xl border border-gray-100" data-testid={`my-attraction-${a.id}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-gold" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-heading text-navy text-sm truncate">{a.name}</p>
                        <p className="text-navy/50 text-xs">{a.visits} visitas</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-navy/60">
                      <span>Net: <strong className="text-emerald-700">€{a.price_net.toFixed(2)}</strong></span>
                      <span>Total: <strong className="text-navy">€{a.price_total.toFixed(2)}</strong></span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent activity */}
      <Card className="bg-white border-gold/20 shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-xl text-navy">Validações Recentes</CardTitle>
            <button
              onClick={() => navigate("/partner/history")}
              className="text-xs text-gold hover:underline font-body"
              data-testid="see-all-history"
            >
              Ver todas →
            </button>
          </div>
        </CardHeader>
        <CardContent>
          {(stats?.recent_visits || []).length === 0 ? (
            <div className="py-6 text-center text-navy/40 text-sm">Nenhuma validação registada ainda</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {stats.recent_visits.map(v => {
                const Icon = ATTRACTION_ICONS[v.attraction_id] || QrCode;
                return (
                  <div key={v.id || v.validated_at} className="flex items-center justify-between py-3" data-testid={`recent-visit-${v.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-body text-sm text-navy font-semibold truncate">{v.passport_holder}</p>
                        <p className="text-xs text-navy/50 truncate">{v.attraction_name} · <span className="font-mono">{v.qr_code}</span></p>
                      </div>
                    </div>
                    <div className="text-right ml-3 flex-shrink-0">
                      <p className="text-xs text-navy">€{v.price_net.toFixed(2)}</p>
                      <p className="text-[10px] text-navy/50">
                        {new Date(v.validated_at).toLocaleDateString("pt-PT")} {new Date(v.validated_at).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// =====================================================
// Scanner / Validate
// =====================================================
const ScannerView = () => {
  const { token, API } = useAuth();
  const [qrCode, setQrCode] = useState("");
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [selectedAttraction, setSelectedAttraction] = useState("");
  const [attractions, setAttractions] = useState([]);
  const [scannerOpen, setScannerOpen] = useState(false);
  const { status, onOnboard, onboardLoading } = useConnectStatus();

  // Auto-open scanner if URL says so (e.g., from Overview quick-action)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("scan") === "1") {
      setScannerOpen(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const searchPassport = async (codeOverride) => {
    const code = (codeOverride ?? qrCode).trim().toUpperCase();
    if (!code) {
      toast.error("Digite ou escaneie o código do passaporte");
      return;
    }
    setLoading(true);
    setPassport(null);
    setSelectedAttraction("");
    try {
      const response = await axios.get(`${API}/partner/validate/${code}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPassport(response.data.passport);
      setAttractions(response.data.attractions);
      setQrCode(code);
      if ((response.data.attractions || []).length === 0) {
        toast.warning("Este passaporte não inclui nenhuma das suas atrações.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Passaporte não encontrado");
    } finally {
      setLoading(false);
    }
  };

  const handleScanned = (decoded) => {
    // QR code may contain just the code (PASS-XXXXXXXX) or a URL; extract the code
    let code = (decoded || "").trim();
    const match = code.match(/PASS-[A-Z0-9]{6,}/i);
    if (match) code = match[0].toUpperCase();
    setScannerOpen(false);
    toast.success(`QR lido: ${code}`);
    searchPassport(code);
  };

  const validateVisit = async () => {
    if (!selectedAttraction) {
      toast.error("Selecione uma atração");
      return;
    }
    setValidating(true);
    try {
      const response = await axios.post(
        `${API}/partner/validate`,
        { passport_id: passport.id, attraction_id: selectedAttraction },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setPassport(null);
      setQrCode("");
      setSelectedAttraction("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao validar passaporte");
    } finally {
      setValidating(false);
    }
  };

  const isAttractionVisited = (attractionId) =>
    passport?.visits?.some(v => v.attraction_id === attractionId);

  const expiryDate = passport ? new Date(passport.expiry_date) : null;
  const isExpired = expiryDate ? expiryDate < new Date() : false;
  const usedCount = passport?.visits?.length || 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl text-navy mb-1">Validar Passaporte</h1>
        <p className="text-navy/60 font-body">Digite o código QR do passaporte do cliente para registar a entrada</p>
      </div>

      <ConnectBanner status={status} onOnboard={onOnboard} loadingOnboard={onboardLoading} />

      <QrScannerModal open={scannerOpen} onClose={() => setScannerOpen(false)} onScan={handleScanned} />

      <Card className="bg-white border-gold/20 shadow-md">
        <CardContent className="pt-6">
          {/* Big SCAN button — prominent on mobile */}
          <Button
            onClick={() => setScannerOpen(true)}
            className="w-full bg-gold hover:bg-gold-dark text-white py-6 rounded-xl text-base mb-4"
            data-testid="open-scan-btn"
          >
            <Camera className="w-5 h-5 mr-2" />
            Scanear QR Code
          </Button>

          <div className="relative flex items-center my-3">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="px-3 text-xs text-navy/40 uppercase">ou introduza manualmente</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <Label htmlFor="qrCode" className="text-navy/80 font-body">Código do Passaporte</Label>
              <Input
                id="qrCode"
                placeholder="Ex: PASS-ABC12345"
                value={qrCode}
                onChange={(e) => setQrCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === "Enter") searchPassport(); }}
                className="mt-1 border-gold/30 focus:border-gold focus:ring-gold/20 text-lg font-mono"
                data-testid="qr-code-input"
              />
            </div>
            <Button
              onClick={() => searchPassport()}
              disabled={loading}
              className="bg-gold hover:bg-gold-dark text-white h-10"
              data-testid="search-passport-btn"
            >
              {loading ? "Buscando..." : <><Search className="mr-2 h-4 w-4" /> Buscar</>}
            </Button>
          </div>
          <p className="text-xs text-navy/40 mt-3">
            💡 Dica: peça ao cliente para abrir o passaporte na app — o código aparece em destaque junto ao QR.
          </p>
        </CardContent>
      </Card>

      {passport && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-white border-gold/20 shadow-lg" data-testid="passport-details-card">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="font-heading text-xl text-navy">Detalhes do Passaporte</CardTitle>
                <Badge className={!isExpired && passport.status === "active" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-red-100 text-red-700 border-red-200"}>
                  {isExpired ? "Expirado" : passport.status === "active" ? "Válido" : "Inválido"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Customer info grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-[10px] text-navy/50 uppercase tracking-wider">Titular</p>
                  <p className="text-navy font-semibold text-sm">{passport.user_name}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-[10px] text-navy/50 uppercase tracking-wider">Validade</p>
                  <p className="text-navy font-semibold text-sm">{expiryDate.toLocaleDateString('pt-PT')}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-[10px] text-navy/50 uppercase tracking-wider">Visitas</p>
                  <p className="text-navy font-semibold text-sm">{usedCount}/{attractions.length}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-[10px] text-navy/50 uppercase tracking-wider">Código</p>
                  <p className="text-navy font-mono text-xs">{passport.qr_code}</p>
                </div>
              </div>

              {/* Attractions selector */}
              <div>
                <Label className="text-navy/80 font-body mb-3 block">Selecione a Atração (apenas as suas)</Label>
                {attractions.length === 0 ? (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                    Este passaporte <strong>não inclui nenhuma das suas atrações</strong>. Confirme que o cliente está no local correto ou contacte o admin.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {attractions.map((attraction) => {
                    const visited = isAttractionVisited(attraction.id);
                    const Icon = ATTRACTION_ICONS[attraction.id] || QrCode;
                    const isSelected = selectedAttraction === attraction.id;
                    return (
                      <button
                        key={attraction.id}
                        onClick={() => !visited && setSelectedAttraction(attraction.id)}
                        disabled={visited}
                        className={`p-4 rounded-xl border text-left transition-all ${
                          visited
                            ? 'bg-gray-100 border-gray-200 cursor-not-allowed opacity-70'
                            : isSelected
                              ? 'bg-gold/10 border-gold ring-2 ring-gold/30'
                              : 'bg-white border-gray-200 hover:border-gold/50 hover:bg-gold/5'
                        }`}
                        data-testid={`select-attraction-${attraction.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-11 h-11 rounded-full flex items-center justify-center ${visited ? 'bg-gray-300 text-gray-500' : isSelected ? 'bg-gold text-white' : 'bg-gold/20 text-gold'}`}>
                            {visited ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`font-body text-sm font-semibold ${visited ? 'text-gray-500' : 'text-navy'}`}>
                              {attraction.name}
                            </p>
                            {visited && <p className="text-gray-400 text-xs">Já visitado</p>}
                            {!visited && isSelected && <p className="text-gold text-xs font-semibold">Selecionado</p>}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                  </div>
                )}
              </div>

              <Button
                onClick={validateVisit}
                disabled={validating || !selectedAttraction || isExpired || passport.status !== "active"}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 rounded-xl text-base"
                data-testid="validate-visit-btn"
              >
                {validating ? "Validando..." : (
                  <>
                    <Check className="mr-2 h-5 w-5" />
                    Validar Entrada
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
};

// =====================================================
// History (with search + filters)
// =====================================================
const HistoryView = () => {
  const { token, API } = useAuth();
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filterAttr, setFilterAttr] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/partner/visits`, { headers: { Authorization: `Bearer ${token}` } });
        setVisits(r.data || []);
      } catch (e) {
        toast.error("Erro ao carregar histórico");
      } finally {
        setLoading(false);
      }
    })();
  }, [API, token]);

  const attractionIds = useMemo(() => {
    const set = new Set();
    visits.forEach(v => set.add(v.attraction_id));
    return Array.from(set);
  }, [visits]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return visits.filter(v => {
      if (filterAttr !== "all" && v.attraction_id !== filterAttr) return false;
      if (!q) return true;
      return (
        (v.passport_holder || "").toLowerCase().includes(q) ||
        (v.attraction_name || "").toLowerCase().includes(q)
      );
    });
  }, [visits, query, filterAttr]);

  const exportCSV = () => {
    const rows = [["Data", "Titular", "Atração", "ID Visita"]];
    filtered.forEach(v => {
      rows.push([
        new Date(v.validated_at).toLocaleString("pt-PT"),
        v.passport_holder,
        v.attraction_name,
        v.id || "",
      ]);
    });
    const csv = rows.map(r => r.map(c => `"${(c || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `validacoes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Exportado!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl text-navy mb-1">Histórico de Validações</h1>
          <p className="text-navy/60 font-body">{filtered.length} de {visits.length} validações</p>
        </div>
        <Button
          onClick={exportCSV}
          disabled={filtered.length === 0}
          variant="outline"
          className="border-gold/30 text-navy hover:bg-gold/10"
          data-testid="export-csv-btn"
        >
          Exportar CSV
        </Button>
      </div>

      <Card className="bg-white border-gold/20 shadow-sm">
        <CardContent className="pt-5 pb-5 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy/40" />
            <Input
              placeholder="Buscar por nome ou atração..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9 border-gold/30"
              data-testid="history-search"
            />
          </div>
          <select
            value={filterAttr}
            onChange={(e) => setFilterAttr(e.target.value)}
            className="bg-white border border-gold/30 rounded-md px-3 py-2 text-sm text-navy"
            data-testid="history-filter-attraction"
          >
            <option value="all">Todas as atrações</option>
            {attractionIds.map(aid => {
              const v = visits.find(x => x.attraction_id === aid);
              return <option key={aid} value={aid}>{v?.attraction_name || aid}</option>;
            })}
          </select>
        </CardContent>
      </Card>

      {filtered.length === 0 ? (
        <Card className="bg-white border-gold/20 shadow-sm">
          <CardContent className="py-12 text-center">
            <Clock className="w-12 h-12 text-gold/50 mx-auto mb-4" />
            <h3 className="font-heading text-xl text-navy mb-2">Nenhuma validação encontrada</h3>
            <p className="text-navy/60 font-body">{visits.length === 0 ? "As validações aparecerão aqui" : "Tente ajustar os filtros"}</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white border-gold/20 shadow-sm">
          <CardContent className="pt-6">
            <div className="divide-y divide-gray-100">
              {filtered.map((visit, index) => {
                const Icon = ATTRACTION_ICONS[visit.attraction_id] || QrCode;
                return (
                  <div
                    key={visit.id || index}
                    className="flex items-center justify-between py-3"
                    data-testid={`history-row-${visit.id || index}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-gold/15 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-gold" />
                      </div>
                      <div>
                        <p className="font-body text-navy font-semibold text-sm">{visit.passport_holder}</p>
                        <p className="text-navy/60 text-xs">{visit.attraction_name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-navy font-body text-sm">{new Date(visit.validated_at).toLocaleDateString('pt-PT')}</p>
                      <p className="text-navy/50 text-xs">{new Date(visit.validated_at).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// =====================================================
// Stripe Payments (kept, slightly polished)
// =====================================================
const StripeConnectView = () => {
  const { token, API } = useAuth();
  const { status, error, loaded, onOnboard, onboardLoading, refresh } = useConnectStatus();

  const handleDashboardLink = async () => {
    try {
      const resp = await axios.post(`${API}/partner/connect/dashboard-link`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.open(resp.data.url, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao abrir dashboard Stripe");
    }
  };

  if (!loaded) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-3xl text-navy mb-1">Pagamentos Stripe</h1>
          <p className="text-navy/60 font-body">Configure a sua conta Stripe para receber transferências automáticas</p>
        </div>
        <Button variant="outline" onClick={refresh} className="border-gold/30 text-navy" data-testid="refresh-stripe-status">
          Atualizar
        </Button>
      </div>

      {error && (
        <Card className="bg-red-50 border-red-200" data-testid="stripe-error-card">
          <CardContent className="pt-5 pb-5">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-heading text-red-900 font-semibold">Não foi possível carregar o estado Stripe</p>
                <p className="text-sm text-red-800 mt-1 font-body">{error}</p>
                <p className="text-xs text-red-700/80 mt-2">
                  Possíveis causas: a chave Stripe em produção não corresponde à conta Connect associada ao seu utilizador, ou o servidor Stripe demorou demasiado a responder. Clique em <strong>Atualizar</strong>. Se persistir, contacte o admin.
                </p>
              </div>
              <Button size="sm" onClick={refresh} className="bg-red-600 hover:bg-red-700 text-white">Tentar de novo</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {status && <ConnectBanner status={status} onOnboard={onOnboard} loadingOnboard={onboardLoading} />}

      {status?.has_account && (
        <Card className="bg-white border-gold/20 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-navy flex items-center gap-2">
              <Banknote className="w-5 h-5 text-gold" /> Estado da Conta
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {status.stripe_error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-xs font-semibold text-red-900 uppercase tracking-wider mb-1">Erro Stripe</p>
                <p className="text-sm text-red-800 font-mono break-all">{status.stripe_error}</p>
                <p className="text-xs text-red-700/80 mt-2">
                  Provavelmente a chave Stripe atual (live/test) não tem acesso a esta conta. Verifique as credenciais em produção.
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-3 bg-gray-50 rounded-lg flex items-center justify-between gap-2">
                <div>
                  <p className="text-xs text-navy/50 font-body uppercase tracking-wider">Account ID</p>
                  <p className="font-mono text-xs text-navy break-all" data-testid="connect-account-id">{status.stripe_account_id}</p>
                </div>
                <Button
                  size="sm" variant="ghost" className="text-navy/50 hover:text-navy"
                  onClick={() => { navigator.clipboard.writeText(status.stripe_account_id); toast.success("Copiado"); }}
                  data-testid="copy-account-id"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-navy/50 font-body uppercase tracking-wider">Detalhes submetidos</p>
                <Badge className={status.stripe_details_submitted ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                  {status.stripe_details_submitted ? "Sim" : "Pendente"}
                </Badge>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-navy/50 font-body uppercase tracking-wider">Cobranças habilitadas</p>
                <Badge className={status.stripe_charges_enabled ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                  {status.stripe_charges_enabled ? "Sim" : "Não"}
                </Badge>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-navy/50 font-body uppercase tracking-wider">Transferências habilitadas</p>
                <Badge className={status.stripe_payouts_enabled ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                  {status.stripe_payouts_enabled ? "Sim" : "Não"}
                </Badge>
              </div>
            </div>

            {status.stripe_requirements?.currently_due?.length > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs font-semibold text-amber-900 uppercase tracking-wider mb-1">Requisitos pendentes</p>
                <ul className="text-sm text-amber-800 list-disc list-inside space-y-0.5">
                  {status.stripe_requirements.currently_due.map((r) => (
                    <li key={r} className="font-mono text-xs">{r}</li>
                  ))}
                </ul>
              </div>
            )}

            {status.stripe_payouts_enabled && (
              <Button
                onClick={handleDashboardLink}
                variant="outline"
                className="w-full border-gold/30 text-navy hover:bg-gold/10 rounded-full mt-2"
                data-testid="connect-dashboard-btn"
              >
                Abrir Dashboard Stripe Express
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Helpful info card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-5 pb-5">
          <h3 className="font-heading text-base text-blue-900 mb-2">Como funcionam as transferências?</h3>
          <ul className="text-sm text-blue-800 font-body space-y-1.5 list-disc list-inside">
            <li>Cada vez que valida uma entrada, é registado o valor Net definido pelo admin.</li>
            <li>O Stripe transfere automaticamente o Net para a sua conta bancária.</li>
            <li>A plataforma fica com a comissão (diferença Total − Net).</li>
            <li>Pode consultar todas as transferências no Dashboard Stripe Express.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

// =====================================================
// QR Scanner Modal (camera) — works on mobile + desktop
// =====================================================
const QrScannerModal = ({ open, onClose, onScan }) => {
  const [error, setError] = useState("");
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    if (!open) return;
    let qr = null;
    let stopped = false;
    const elementId = "qr-reader";

    const start = async () => {
      setError("");
      try {
        qr = new Html5Qrcode(elementId);
        setScanning(true);
        await qr.start(
          { facingMode: { exact: "environment" } },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => {
            if (stopped) return;
            stopped = true;
            onScan(decodedText);
          },
          () => {} // silent decode error
        ).catch(async () => {
          // Some browsers / front-cam-only devices fail with "exact: environment".
          // Fallback to generic environment, then to user-facing.
          try {
            await qr.start(
              { facingMode: "environment" },
              { fps: 10, qrbox: { width: 250, height: 250 } },
              (decodedText) => { if (!stopped) { stopped = true; onScan(decodedText); } },
              () => {}
            );
          } catch (e2) {
            try {
              await qr.start(
                { facingMode: "user" },
                { fps: 10, qrbox: { width: 250, height: 250 } },
                (decodedText) => { if (!stopped) { stopped = true; onScan(decodedText); } },
                () => {}
              );
            } catch (e3) {
              setError("Não foi possível aceder à câmara. Verifique permissões e tente novamente.");
              setScanning(false);
            }
          }
        });
      } catch (e) {
        setError("Erro ao iniciar scanner: " + (e?.message || e));
        setScanning(false);
      }
    };

    start();

    return () => {
      stopped = true;
      (async () => {
        try {
          if (qr && qr.getState && qr.getState() === 2) {
            await qr.stop();
            await qr.clear();
          }
        } catch { /* ignore */ }
      })();
    };
    // eslint-disable-next-line
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="qr-scanner-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-gold" />
            <h3 className="font-heading text-navy">Scanear QR Code</h3>
          </div>
          <button
            onClick={onClose}
            className="text-navy/60 hover:text-navy p-1"
            data-testid="qr-scanner-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4">
          {error ? (
            <div className="py-8 text-center">
              <CameraOff className="w-10 h-10 text-red-500 mx-auto mb-3" />
              <p className="text-red-700 text-sm">{error}</p>
              <p className="text-navy/50 text-xs mt-2">
                Dica: aceda a partir de um browser que suporte câmara (Safari, Chrome) e autorize quando pedido.
              </p>
            </div>
          ) : (
            <>
              <div id="qr-reader" className="w-full rounded-lg overflow-hidden bg-black aspect-square" />
              <p className="text-navy/60 text-xs mt-3 text-center">
                Aponte a câmara para o QR Code do cliente
              </p>
              {!scanning && (
                <p className="text-navy/40 text-xs mt-1 text-center">A iniciar câmara...</p>
              )}
            </>
          )}
        </div>
        <div className="p-3 bg-gray-50 border-t flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="qr-scanner-cancel">Cancelar</Button>
        </div>
      </div>
    </div>
  );
};

// =====================================================
// Real-time Notifications Bell (polling 15s)
// =====================================================
const NotificationsBell = () => {
  const { token, API } = useAuth();
  const [events, setEvents] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [lastSeen, setLastSeen] = useState(() => localStorage.getItem("partner_notif_last_seen") || "");

  useEffect(() => {
    let mounted = true;
    let timer = null;
    let firstFetch = true;

    const fetchOnce = async () => {
      try {
        const url = lastSeen
          ? `${API}/partner/notifications?since=${encodeURIComponent(lastSeen)}`
          : `${API}/partner/notifications`;
        const resp = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!mounted) return;
        const incoming = resp.data?.events || [];
        if (firstFetch) {
          // Initial load: just populate, no toast
          setEvents(incoming);
          firstFetch = false;
        } else if (incoming.length > 0) {
          // New events since last poll → toast + prepend to list + bump unread
          setEvents((prev) => [...incoming, ...prev].slice(0, 30));
          setUnread((u) => u + incoming.length);
          incoming.forEach((ev) => {
            const desc = ev.type === "sale"
              ? `${ev.message} · €${(ev.amount || 0).toFixed(2)}`
              : ev.message;
            toast.success(ev.title, { description: desc });
          });
        }
        // Advance the cursor to server time so next poll only sees newer events
        if (resp.data?.server_time) {
          setLastSeen(resp.data.server_time);
          localStorage.setItem("partner_notif_last_seen", resp.data.server_time);
        }
      } catch (e) {
        // silent
      }
    };

    fetchOnce();
    timer = setInterval(fetchOnce, 15000);
    return () => { mounted = false; if (timer) clearInterval(timer); };
    // eslint-disable-next-line
  }, [API, token]);

  const togglePanel = () => {
    setOpen(!open);
    if (!open) setUnread(0);
  };

  return (
    <div className="relative">
      <button
        onClick={togglePanel}
        className="relative w-10 h-10 rounded-full bg-white border border-gold/30 text-navy hover:bg-gold/10 flex items-center justify-center transition shadow-sm"
        data-testid="notifications-bell"
        aria-label="Notificações"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-2 w-80 sm:w-96 bg-white border border-gold/20 rounded-2xl shadow-2xl z-50 overflow-hidden"
            data-testid="notifications-panel"
          >
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-heading text-navy">Notificações</h3>
              <span className="text-xs text-navy/40">Atualiza a cada 15s</span>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {events.length === 0 ? (
                <div className="py-10 text-center text-navy/40 text-sm">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  Sem notificações ainda
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {events.map((ev) => {
                    const isSale = ev.type === "sale";
                    return (
                      <div key={ev.id} className="p-3 flex items-start gap-3 hover:bg-gray-50" data-testid={`notif-${ev.id}`}>
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isSale ? "bg-gold/15 text-gold" : "bg-emerald-100 text-emerald-600"
                        }`}>
                          {isSale ? <Euro className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-navy">{ev.title}</p>
                          <p className="text-xs text-navy/60 leading-snug">{ev.message}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {isSale && (
                              <span className="text-[11px] font-semibold text-gold">€{(ev.amount || 0).toFixed(2)}</span>
                            )}
                            <span className="text-[10px] text-navy/40">
                              {ev.at ? new Date(ev.at).toLocaleString("pt-PT") : ""}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// =====================================================
// Main Partner Dashboard
// =====================================================
export default function PartnerDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Sessão encerrada com sucesso");
  };

  const navItems = [
    { icon: Home, label: "Início", path: "/partner" },
    { icon: QrCode, label: "Validar", path: "/partner/validate" },
    { icon: Clock, label: "Histórico", path: "/partner/history" },
    { icon: Banknote, label: "Pagamentos", path: "/partner/payments" },
  ];

  const isActive = (path) => location.pathname === path || (path === "/partner" && location.pathname === "/partner/");

  return (
    <div className="min-h-screen bg-sand">
      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-40 h-screen w-64 bg-navy transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/10">
            <PassAlgarveLogo size="small" />
            <Badge className="mt-2 bg-gold/20 text-gold border-gold/30">Parceiro</Badge>
          </div>

          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                className={`sidebar-link w-full text-left ${isActive(item.path) ? 'bg-white/10 text-white' : ''}`}
                data-testid={`nav-${item.label.toLowerCase().replace('í','i').replace('ó','o')}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            ))}
          </nav>

          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 mb-4 px-2">
              <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                <span className="text-gold font-semibold">{user?.name?.[0]?.toUpperCase()}</span>
              </div>
              <div className="min-w-0">
                <p className="text-white font-body text-sm truncate">{user?.name}</p>
                <p className="text-white/50 text-xs truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full text-white/70 hover:text-white hover:bg-white/10 justify-start"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Sair
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-navy p-4 flex items-center justify-between">
        <PassAlgarveLogoCompact />
        <div className="flex items-center gap-2">
          <NotificationsBell />
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white p-2"
            data-testid="mobile-sidebar-toggle"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <main className="lg:ml-64 p-4 lg:p-8 pt-28 lg:pt-8">
        <div className="max-w-6xl mx-auto">
          {/* Top-right notifications bell — desktop */}
          <div className="hidden lg:flex justify-end mb-2">
            <NotificationsBell />
          </div>
          <Routes>
            <Route index element={<OverviewView />} />
            <Route path="validate" element={<ScannerView />} />
            <Route path="history" element={<HistoryView />} />
            <Route path="payments" element={<StripeConnectView />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
